import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import isNil from 'utils/isNil';
import { colorTypeHelper } from 'utils/colorUtility';
import styled from 'styled-components';
import SweetScrollContext from 'utils/SweetScrollContext';
import { Accordion as MuiAccordion, AccordionSummary, AccordionDetails } from '@mui/material';
import useHash from 'hooks/useHash';
import checkIsHeadingInHash from 'utils/checkIsHeadingInHash';
import { ComponentMap, ReactLazyComponent } from '../../Contentful/ComponentMap';

const RenderChildComponents = ({ components }) =>
  components?.map((item, index) => {
    const Component = ComponentMap[item.__typename];
    return Component ? (
      <ReactLazyComponent>
        <Component {...item} key={index} />
      </ReactLazyComponent>
    ) : null;
  }) || null;

const Accordion = ({
  backgroundColor: background,
  border,
  heading,
  collapseProps,
  components,
  customHeading,
  anchorId,
  styles,
  id = 'websiteAccordion',
  ...otherProps
}) => {
  const [expanded, setExpanded] = useState(false);
  const backgroundColor = colorTypeHelper[background];
  const [hash] = useHash();

  const { scrollToAnchor } = useContext(SweetScrollContext);

  useEffect(() => {
    const isTitleInHash = checkIsHeadingInHash(hash, heading);
    const expand = isTitleInHash || `#${anchorId}` === hash;
    setExpanded(expand);

    if (isTitleInHash) {
      scrollToAnchor(`#${anchorId}`, { updateURL: false });
    }
  }, [hash]);

  return (
    <Container
      {...styles}
      background={`var(${backgroundColor})`}
      border={border}
      id={anchorId || id}
      {...otherProps}
    >
      {expanded && !isNil(components) && <RenderChildComponents components={components} />}
      {!expanded && (
        <MuiAccordion expanded={expanded}>
          <AccordionSummary onClick={() => setExpanded(true)}>
            <h3 className="font-heading-md">{heading}</h3>
          </AccordionSummary>
          <AccordionDetails>
            <RenderChildComponents components={components} />
          </AccordionDetails>
        </MuiAccordion>
      )}
    </Container>
  );
};

Accordion.defaultProps = {
  border: true,
};

Accordion.propTypes = {
  border: PropTypes.bool,
  background: PropTypes.object,
  title: PropTypes.string,
  components: PropTypes.array,
};

export default Accordion;

const Container = styled.div`
  background: ${({ background }) => background};

  .MuiAccordion-root {
    border: none;
    box-shadow: none;
    margin: 0;

    .MuiAccordionDetails-root {
      padding: 0;
    }

    .MuiAccordionSummary-content {
      padding: 15px;
      max-width: 784px;
      text-align: center;
      justify-content: center;
      border: ${(props) => (props.border ? '1px solid var(--grey-3)' : 'none')};
      border-radius: 8px;

      h3 {
        margin: 0;
      }
    }

    .MuiCollapse-root {
      padding: 13px 32px 0;
      background: ${(props) => props.accordionBgColor};

      .MuiCollapse-wrapper {
        padding-bottom: 32px;

        @media (max-width: 575px) {
          padding: 0;
        }
      }
    }

    &.Mui-expanded {
      .MuiAccordionSummary-root {
        display: none;
      }
    }
  }

  @media (max-width: 575px) {
    margin: ${({ marginMobile }) => marginMobile || '0 20px 0 20px'};

    .MuiAccordion-root {
      .MuiCollapse-root {
        padding: 0;
      }

      .MuiAccordionSummary-root {
        padding: 0;
      }
    }
  }
`;
